/**
 * Churn Prediction Badge Component
 * Displays ML-predicted churn probability with visual indicators
 *
 * Trust Marketplace - ML Prediction System
 *
 * MIGRATED: MUI -> Tailwind CSS components
 */

import React from 'react';
import {
  AlertTriangle,
  AlertCircle,
  CheckCircle,
  TrendingUp,
  TrendingDown,
  Brain,
  Info,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

export interface ContributingFactor {
  factor: string;
  value: number | string;
  importance: number;
  impact: 'high' | 'medium' | 'low';
}

export interface ChurnPrediction {
  churn_probability_7d?: number;
  churn_probability_14d?: number;
  churn_probability_30d?: number;
  ml_risk_level?: 'low' | 'moderate' | 'high' | 'critical';
  ml_model_version?: string;
  prediction_timestamp?: string;
  contributing_factors?: ContributingFactor[];
}

interface ChurnPredictionBadgeProps {
  prediction?: ChurnPrediction;
  horizon?: 7 | 14 | 30;
  showDetails?: boolean;
  size?: 'small' | 'medium';
  variant?: 'badge' | 'chip' | 'inline';
}

// Get risk level from probability
const getRiskLevel = (probability: number): 'low' | 'moderate' | 'high' | 'critical' => {
  if (probability >= 0.7) return 'critical';
  if (probability >= 0.4) return 'high';
  if (probability >= 0.2) return 'moderate';
  return 'low';
};

// Get Tailwind color classes based on risk level
const getRiskColorClasses = (riskLevel: string): { bg: string; text: string; progressBg: string } => {
  switch (riskLevel) {
    case 'critical':
      return { bg: 'bg-red-100 dark:bg-red-900/50', text: 'text-red-700 dark:text-red-200', progressBg: 'bg-red-500' };
    case 'high':
      return { bg: 'bg-yellow-100 dark:bg-yellow-900/50', text: 'text-yellow-700 dark:text-yellow-200', progressBg: 'bg-yellow-500' };
    case 'moderate':
      return { bg: 'bg-blue-100 dark:bg-blue-900/50', text: 'text-blue-700 dark:text-blue-200', progressBg: 'bg-blue-500' };
    case 'low':
    default:
      return { bg: 'bg-green-100 dark:bg-green-900/50', text: 'text-green-700 dark:text-green-200', progressBg: 'bg-green-500' };
  }
};

// Get icon based on risk level
const RiskIcon = ({ riskLevel, className = '' }: { riskLevel: string; className?: string }) => {
  const iconClass = `h-4 w-4 ${className}`;
  switch (riskLevel) {
    case 'critical':
      return <AlertCircle className={`${iconClass} text-red-600 dark:text-red-400`} />;
    case 'high':
      return <AlertTriangle className={`${iconClass} text-yellow-600 dark:text-yellow-400`} />;
    case 'moderate':
      return <Info className={`${iconClass} text-blue-600 dark:text-blue-400`} />;
    case 'low':
    default:
      return <CheckCircle className={`${iconClass} text-green-600 dark:text-green-400`} />;
  }
};

// Format factor name for display
const formatFactorName = (factor: string): string => {
  return factor
    .replace(/^f_/, '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (l) => l.toUpperCase());
};

// Get impact color classes
const getImpactColorClass = (impact: string): string => {
  switch (impact) {
    case 'high':
      return 'text-red-600 dark:text-red-400';
    case 'medium':
      return 'text-orange-600 dark:text-orange-400';
    case 'low':
    default:
      return 'text-green-600 dark:text-green-400';
  }
};

export const ChurnPredictionBadge: React.FC<ChurnPredictionBadgeProps> = ({
  prediction,
  horizon = 14,
  showDetails = true,
  size = 'medium',
  variant = 'chip',
}) => {
  // Get probability based on horizon
  const getProbability = (): number | undefined => {
    if (!prediction) return undefined;
    switch (horizon) {
      case 7:
        return prediction.churn_probability_7d;
      case 14:
        return prediction.churn_probability_14d;
      case 30:
        return prediction.churn_probability_30d;
      default:
        return prediction.churn_probability_14d;
    }
  };

  const probability = getProbability();

  // If no prediction available, show placeholder
  if (probability === undefined || probability === null) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge variant="outline" className="opacity-50 gap-1">
              <Brain className="h-3 w-3" />
              <span>--</span>
            </Badge>
          </TooltipTrigger>
          <TooltipContent>ML prediction not available</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  const riskLevel = prediction?.ml_risk_level || getRiskLevel(probability);
  const colors = getRiskColorClasses(riskLevel);
  const percentDisplay = Math.round(probability * 100);

  // Badge variant - just the percentage
  if (variant === 'badge') {
    const badgeSize = size === 'small' ? 'h-8 w-8 text-xs' : 'h-10 w-10 text-sm';

    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div
              className={`inline-flex items-center justify-center rounded-full font-bold cursor-${showDetails ? 'pointer' : 'default'} ${badgeSize} ${colors.bg} ${colors.text}`}
            >
              {percentDisplay}%
            </div>
          </TooltipTrigger>
          <TooltipContent>{horizon}-day churn probability: {percentDisplay}%</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  // Inline variant - text with icon
  if (variant === 'inline') {
    return (
      <div className="inline-flex items-center gap-1">
        <RiskIcon riskLevel={riskLevel} />
        <span className={`${size === 'small' ? 'text-xs' : 'text-sm'} font-medium ${colors.text}`}>
          {percentDisplay}% churn risk
        </span>
      </div>
    );
  }

  // Default chip variant with popover for details
  const chipContent = (
    <Badge
      className={`gap-1 cursor-${showDetails ? 'pointer' : 'default'} ${colors.bg} ${colors.text} border-0 ${
        riskLevel === 'critical' ? 'animate-pulse' : ''
      }`}
    >
      <Brain className="h-3 w-3" />
      <span>{percentDisplay}% ({horizon}d)</span>
    </Badge>
  );

  if (!showDetails || !prediction?.contributing_factors?.length) {
    return chipContent;
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        {chipContent}
      </PopoverTrigger>
      <PopoverContent className="w-80 p-4">
        <div className="space-y-3">
          {/* Header */}
          <div className="flex items-center gap-2">
            <Brain className="h-4 w-4 text-primary" />
            <h4 className="font-semibold text-sm">AI Churn Prediction</h4>
          </div>

          {/* Probability Bar */}
          <div className="space-y-1">
            <div className="flex justify-between text-xs">
              <span className="text-muted-foreground">{horizon}-Day Churn Probability</span>
              <span className={`font-bold ${colors.text}`}>{percentDisplay}%</span>
            </div>
            <div className="relative h-2 w-full overflow-hidden rounded-full bg-gray-200">
              <div
                className={`h-full transition-all ${colors.progressBg}`}
                style={{ width: `${percentDisplay}%` }}
              />
            </div>
          </div>

          {/* Risk Level */}
          <div className="flex items-center gap-2">
            <RiskIcon riskLevel={riskLevel} />
            <span className="text-sm">
              Risk Level: <strong className="capitalize">{riskLevel}</strong>
            </span>
          </div>

          {/* Contributing Factors */}
          {prediction?.contributing_factors && prediction.contributing_factors.length > 0 && (
            <>
              <hr className="border-border" />
              <div>
                <p className="text-xs text-muted-foreground mb-2">Top Contributing Factors:</p>
                <ul className="space-y-2">
                  {prediction.contributing_factors.slice(0, 5).map((factor, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <div className="mt-0.5">
                        {factor.impact === 'high' ? (
                          <TrendingUp className={`h-4 w-4 ${getImpactColorClass(factor.impact)}`} />
                        ) : (
                          <TrendingDown className={`h-4 w-4 ${getImpactColorClass(factor.impact)}`} />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium">{formatFactorName(factor.factor)}</p>
                        <p className="text-xs text-muted-foreground">
                          {typeof factor.value === 'number'
                            ? factor.value.toFixed(1)
                            : factor.value}
                          {' · '}
                          <span className={getImpactColorClass(factor.impact)}>
                            {factor.impact} impact
                          </span>
                        </p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </>
          )}

          {/* Model Info */}
          {prediction?.ml_model_version && (
            <p className="text-xs text-muted-foreground mt-2">
              Model: {prediction.ml_model_version}
            </p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default ChurnPredictionBadge;
